import { HubConnectionState } from "@microsoft/signalr";
import { nanoid } from "@reduxjs/toolkit";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { ChatJobUpdateType, ChatMessageType, type AppendChatMessagesRequest, type AppendChatMessagesResponse, type ChatConversationDto, type ChatJobUpdatedNotification, type ChatMessageAppendDto, type ChatMessageDto, type DeleteLastToolCallResponse } from "../../contracts";
import { realtimeEvents } from "../../features/signalr/realtimeEvents";
import { getSignalRStatus } from "../../features/signalr/signalRStatus";
import { useSignalR } from "../../features/signalr/useSignalR";
import { useViewContext } from "../../hooks/useViewContext";
import { tryParseJson } from "../../json/parsing";
import { isCommercialSimulatorAgentResponse, isCommercialSimulatorSubBrandAgentResponse } from "../../models/agentResponseModels";
import { getLastUserText, type ChatMessageViewModel } from "../../models/chatMessageViewModel";
import { isCurrentMessageToolRequest, type CommercialSimulatorOptions, type CommercialSimulatorSubBrandOptions } from "../../models/toolRequestModels";
import { appendMessage, clearConversation, removeMessageByClientId, removePendingToolRequest, selectConversation, setConversation, setConversationBusy, setCurrentCommercialPlan } from "../../slices/chatSlice";
import { ChatMessageDispatcher } from "./chatMessageDispatcher";
import { isSimulatorAgentRequest } from "../../models/agentRequestModels";

export type ChatControllerActions = {
    startSimulationAsync: (
        selectedOptions: CommercialSimulatorOptions,
        message: ChatMessageDto
    ) => Promise<boolean>;
    startSubBrandSimulationAsync: (
        selectedOptions: CommercialSimulatorSubBrandOptions,
        message: ChatMessageDto
    ) => Promise<boolean>;
    cancelToolCallAsync: () => Promise<void>;
};

export type ConversationChangedOptions = {
    replace?: boolean;
};

type UseChatControllerProps = {
    onConversationChanged?: (conversationId: number | undefined, options?: ConversationChangedOptions) => void;
};

const emptyMessages: ChatMessageViewModel[] = [];

export const checkIsSimulationRunning = (
    messages: ChatMessageViewModel[] | undefined
): boolean => {
    const lastMessage = [...(messages ?? [])]
        .sort((a, b) => (b.sequence ?? 0) - (a.sequence ?? 0))
        .at(0);

    return lastMessage == null
        ? false
        : isSimulatorAgentRequest(lastMessage.type, lastMessage.action, lastMessage.payload);
};

export const useChatController = ({
    onConversationChanged
}: UseChatControllerProps) => {
    const appDispatch = useAppDispatch();
    const viewContext = useViewContext();
    const location = useLocation();
    const chatConversation = useAppSelector(selectConversation);

    const chatMessages = chatConversation?.messages ?? emptyMessages;
    const activeConversationId = chatConversation?.id;

    const activeConversationIdRef = useRef<number | undefined>(activeConversationId);
    const chatMessagesRef = useRef<ChatMessageViewModel[]>(chatMessages);

    const [query, setQuery] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isCancellingToolCall, setIsCancellingToolCall] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | undefined>();

    const isSimulationRunning = checkIsSimulationRunning(chatMessages);
    const [hasStartedSimulation, setHasStartedSimulation] = useState(isSimulationRunning);

    const { connection, connectionState } = useSignalR();

    const dispatcherRef = useRef<ChatMessageDispatcher | null>(null);

    if (!dispatcherRef.current) {
        dispatcherRef.current = new ChatMessageDispatcher({
            onTimeout: () => {
                setHasError(true);
                setHasStartedSimulation(false);
            },
            onPendingQueueEmpty: () => {
                setIsSubmitting(false);
                setHasStartedSimulation(false);
            }
        });
    }

    const clearPendingResponses = useCallback((): void => {
        dispatcherRef.current?.clearAllPendingResponses();
    }, []);

    const clearError = useCallback((): void => {
        setHasError(false);
        setErrorMessage(undefined);
    }, []);

    const notifyConversationChanged = useCallback((conversationId: number | undefined, options?: ConversationChangedOptions): void => {
        if (conversationId != null && activeConversationIdRef.current === conversationId) {
            return;
        }

        activeConversationIdRef.current = conversationId;
        onConversationChanged?.(conversationId, options);
    }, [onConversationChanged]);

    const replaceConversation = useCallback((conversation: ChatConversationDto | null, options?: ConversationChangedOptions): void => {
        clearPendingResponses();
        appDispatch(setConversation(conversation));
        notifyConversationChanged(conversation?.id, options);
        setQuery("");
        setIsSubmitting(false);
        setIsCancellingToolCall(false);
        setHasStartedSimulation(false);
        clearError();
    }, [appDispatch, clearError, clearPendingResponses, notifyConversationChanged]);

    const startNewConversation = useCallback((): void => {
        clearPendingResponses();
        appDispatch(clearConversation());
        notifyConversationChanged(undefined);
        setQuery("");
        setIsSubmitting(false);
        setIsCancellingToolCall(false);
        setHasStartedSimulation(false);
        clearError();
    }, [appDispatch, clearError, clearPendingResponses, notifyConversationChanged]);

    const submitQueryAsync = useCallback(async (): Promise<void> => {
        const trimmedQuery = query.trim();
        if (!trimmedQuery
            || !connection
            || connectionState !== HubConnectionState.Connected
            || isSubmitting) {
            return;
        }

        setHasError(false);
        clearPendingResponses();

        const clientId = nanoid();

        const viewContextData = viewContext.getViewContext();
        const currentUrl: string = `${location.pathname}${location.search}${location.hash}`;

        const userMessage: ChatMessageAppendDto = {
            client_message_id: clientId,
            chat_conversation_id: activeConversationId,
            text: trimmedQuery,
            type: ChatMessageType.User,
            view_route: currentUrl,
            view_context_json: viewContextData ? JSON.stringify(viewContextData) : null
        };

        appDispatch(appendMessage(userMessage));
        setQuery("");
        setIsSubmitting(true);

        try {
            const newMessage: AppendChatMessagesRequest = {
                chat_conversation_id: activeConversationId,
                conversation: [userMessage]
            };

            dispatcherRef.current?.waitForResponseMessages([userMessage.client_message_id!]);
            const response = await connection.invoke<AppendChatMessagesResponse>("Chat", newMessage);

            // is a newly created conversation, maybe refactor in future?
            if (response.chat_conversation) {
                appDispatch(setConversation(response.chat_conversation));
                notifyConversationChanged(response.chat_conversation.id);
            }
        } catch (error) {
            clearPendingResponses();
            appDispatch(removeMessageByClientId(clientId));
            setQuery(trimmedQuery);
            setIsSubmitting(false);
            setErrorMessage(error instanceof Error ? error.message : "An unknown error occurred while submitting your message.");
            setHasError(true);
            console.error("Error submitting simulator chat message.", error);
        }
    }, [viewContext, activeConversationId, appDispatch, clearPendingResponses, location,
        connection, connectionState, isSubmitting, notifyConversationChanged, query]);

    const startCommercialSimulationAsync = useCallback(async (
        selectedOptions: CommercialSimulatorOptions | CommercialSimulatorSubBrandOptions,
        message: ChatMessageDto,
        action: "commercial_simulator_sku" | "commercial_simulator_sub_brand"
    ): Promise<boolean> => {
        if (!connection
            || activeConversationId == null
            || !message?.payload
            || connectionState !== HubConnectionState.Connected
            || isSubmitting) {
            return false;
        }

        setHasError(false);
        clearPendingResponses();
        setIsSubmitting(true);
        setHasStartedSimulation(true);

        try {
            const userToolResult = {
                tool_call: message.payload,
                tool_call_user_selection: selectedOptions
            };

            const toolResponse: ChatMessageAppendDto = {
                client_message_id: nanoid(),
                chat_conversation_id: activeConversationId,
                json: JSON.stringify(userToolResult),
                type: ChatMessageType.ToolResponse
            };

            const simulationMessage: ChatMessageAppendDto = {
                client_message_id: nanoid(),
                chat_conversation_id: activeConversationId,
                json: JSON.stringify(userToolResult),
                action,
                type: ChatMessageType.AgentRequest
            };

            appDispatch(appendMessage(toolResponse));
            appDispatch(appendMessage(simulationMessage));

            dispatcherRef.current?.waitForResponseMessages([
                simulationMessage.client_message_id!
            ]);

            const response = await connection.invoke<AppendChatMessagesResponse>("Chat", {
                chat_conversation_id: activeConversationId,
                conversation: [toolResponse, simulationMessage]
            } satisfies AppendChatMessagesRequest);

            if (response.chat_conversation_id !== activeConversationId) {
                throw new Error("The server returned a different conversation id for the simulation request.");
            }

            return true;
        } catch (error) {
            clearPendingResponses();
            setErrorMessage(error instanceof Error ? error.message : "An unknown error occurred while starting the simulation.");
            setHasError(true);
            setIsSubmitting(false);
            setHasStartedSimulation(false);
            console.error("Error submitting simulator start simulation message.", error);
            return false;
        }
    }, [activeConversationId, appDispatch, clearPendingResponses, connection, connectionState, isSubmitting]);

    const startSubBrandSimulationAsync = useCallback(async (
        selectedOptions: CommercialSimulatorSubBrandOptions,
        message: ChatMessageDto
    ): Promise<boolean> => startCommercialSimulationAsync(
        selectedOptions,
        message,
        "commercial_simulator_sub_brand"
    ), [startCommercialSimulationAsync]);

    const startSimulationAsync = useCallback(async (
        selectedOptions: CommercialSimulatorOptions,
        message: ChatMessageDto
    ): Promise<boolean> => startCommercialSimulationAsync(
        selectedOptions,
        message,
        "commercial_simulator_sku"
    ), [startCommercialSimulationAsync]);

    const cancelToolCallAsync = useCallback(async (): Promise<void> => {
        const isInToolCall = isCurrentMessageToolRequest(chatMessagesRef.current);
        if (!connection
            || !isInToolCall
            || activeConversationId == null
            || isCancellingToolCall
            || isSubmitting) {
            return;
        }

        setHasError(false);
        setIsCancellingToolCall(true);

        try {
            await connection.invoke<DeleteLastToolCallResponse>("DeleteLastToolCall", {
                chat_conversation_id: activeConversationId
            });

            const restoredQuery = getLastUserText(chatMessagesRef.current);

            if (restoredQuery) {
                setQuery(restoredQuery);
            }

            appDispatch(removePendingToolRequest());
        } catch (error) {
            console.error("Error cancelling active simulator tool call.", error);
            setHasError(true);
        } finally {
            setIsCancellingToolCall(false);
        }
    }, [activeConversationId, appDispatch, connection, isCancellingToolCall, isSubmitting]);

    useEffect(() => {
        activeConversationIdRef.current = activeConversationId;
    }, [activeConversationId]);

    useEffect(() => {
        chatMessagesRef.current = chatMessages;
    }, [chatMessages]);

    useEffect(() => {
        if (!connection) {
            return;
        }

        const handleChatMessage = (message: ChatMessageDto): void => {
            const activeConversationId = activeConversationIdRef.current;
            if (activeConversationId == null || activeConversationId !== message.chat_conversation_id) {
                return;
            }
            if (isCommercialSimulatorAgentResponse(message.type, message.action, message.payload)
                || isCommercialSimulatorSubBrandAgentResponse(message.type, message.action, message.payload)) {
                appDispatch(setCurrentCommercialPlan({
                    currentCommercialPlan: message.payload,
                    currentCommercialPlanMessageId: message.id
                }));
            }

            appDispatch(appendMessage(message));
        };

        const handleChatJobUpdated = (update: ChatJobUpdatedNotification): void => {
            switch (update.type) {
                case ChatJobUpdateType.Queued:
                    break;
                case ChatJobUpdateType.Completed:
                case ChatJobUpdateType.Failed:
                case ChatJobUpdateType.Refused:
                    if (activeConversationIdRef.current === update.chat_conversation_id) {
                        appDispatch(setConversationBusy({ is_busy: update.is_conversation_busy }));
                        if (update.result_chat_message) {
                            appDispatch(appendMessage(update.result_chat_message));
                        }
                        if ((update.chat_job.errors && update.chat_job.errors.length)
                            || (update.chat_job.refusals && update.chat_job.refusals.length)) {
                            const errors = tryParseJson<string[]>(update.chat_job.errors) ?? [];
                            const refusals = tryParseJson<string[]>(update.chat_job.refusals) ?? [];

                            setErrorMessage([...errors, ...refusals].join("; "));
                            setHasError(true);
                        }
                    }

                    dispatcherRef.current?.resolvePendingResponse(update.source_chat_message.client_message_id, update.type !== ChatJobUpdateType.Completed);
                    break;
            }
        };

        connection.on(realtimeEvents.messageCreated, handleChatMessage);
        connection.on(realtimeEvents.chatJobUpdated, handleChatJobUpdated);

        return () => {
            connection.off(realtimeEvents.messageCreated, handleChatMessage);
            connection.off(realtimeEvents.chatJobUpdated, handleChatJobUpdated);
        };
    }, [appDispatch, connection, viewContext]);

    useEffect(() => () => {
        dispatcherRef.current?.clearAllPendingResponses();
    }, []);

    const actions = useMemo<ChatControllerActions>(() => ({
        startSimulationAsync,
        startSubBrandSimulationAsync,
        cancelToolCallAsync
    }), [
        startSimulationAsync,
        startSubBrandSimulationAsync,
        cancelToolCallAsync
    ]);

    return {
        chatConversation,
        chatMessages,
        query,
        setQuery,
        hasError,
        errorMessage,
        clearError,
        isChatBusy: Boolean(chatConversation?.is_busy || isSubmitting),
        signalRStatus: getSignalRStatus(connectionState),
        connectionState,
        submitQueryAsync,
        actions,
        replaceConversation,
        startNewConversation,
        hasStartedSimulation
    };
};
